import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  Crown,
  Loader2,
  Receipt,
  Plus,
  Download,
  CheckCircle2,
  XCircle,
  Calendar,
  X,
  Tag,
  FileText,
} from "lucide-react";
import SettingsLayout from "./SettingsLayout";
import EmptyState from "../../Components/EmptyState";
import useSettingsProfile from "./useSettingsProfile";
import { fetchMyPaymentHistory } from "../../services/api";
import { mergePaymentHistory, paymentRecordToReceipt } from "../../utils/paymentReceipt";
import {
  formatAmountPaise,
  formatMembershipDate,
  parseMembershipDate,
  PREMIUM_DURATION_LABEL,
} from "../../utils/membership";
import { downloadPaymentReceipt } from "../../utils/printReceipt";

export default function PaymentHistorySettings() {
  const { user, profile, loading } = useSettingsProfile();
  const paymentHistoryQuery = useQuery({
    queryKey: ["myPaymentHistory", user?.uid],
    queryFn: async () => {
      if (!user) return [];
      const idToken = await user.getIdToken();
      const result = await fetchMyPaymentHistory(idToken);
      return result.payments || [];
    },
    enabled: !!user,
    staleTime: 300000,
    cacheTime: 600000,
    retry: false,
  });

  const paymentHistory = paymentHistoryQuery.data || [];
  const historyLoading = paymentHistoryQuery.isLoading;
  const [selectedPayment, setSelectedPayment] = useState(null);

  const allPayments = useMemo(
    () => mergePaymentHistory(paymentHistory, profile),
    [paymentHistory, profile]
  );

  const handleDownload = (payment) => {
    try {
      downloadPaymentReceipt(payment);
      toast.success("Receipt downloaded successfully");
    } catch {
      toast.error("Failed to download receipt. Please try again.");
    }
  };

  return (
    <SettingsLayout
      title="Payment history"
      description="Your premium receipts and invoices."
    >
      {historyLoading || loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-brand-600" size={28} />
        </div>
      ) : allPayments.length === 0 ? (
        <EmptyState
          Icon={Receipt}
          title="No payments yet"
          description="Your premium receipts will appear here after you upgrade."
          action={
            <Link to="/settings/membership" className="btn-primary px-5">
              <Plus size={14} /> Upgrade now
            </Link>
          }
        />
      ) : (
        <>
          {/* Payment list */}
          <section>
            <p className="px-1 text-[11.5px] font-bold uppercase tracking-wider text-ink-400">
              Recent transactions
            </p>
            <div className="mt-2 overflow-hidden rounded-2xl border border-sand-200 bg-white">
              {allPayments.map((payment, idx) => (
                <PaymentCard
                  key={payment.id || payment.paymentId || idx}
                  payment={payment}
                  onView={() => setSelectedPayment(payment)}
                  onDownload={() => handleDownload(payment)}
                />
              ))}
            </div>
          </section>
        </>
      )}

      {/* Detail bottom sheet */}
      <AnimatePresence>
        {selectedPayment && (
          <PaymentDetailSheet
            payment={selectedPayment}
            onClose={() => setSelectedPayment(null)}
            onDownload={() => handleDownload(selectedPayment)}
          />
        )}
      </AnimatePresence>
    </SettingsLayout>
  );
}

/* =====================================================================
   Payment Card
===================================================================== */
function PaymentCard({ payment, onView, onDownload }) {
  const r = paymentRecordToReceipt(payment);
  const paidLabel = formatMembershipDate(parseMembershipDate(r.paidAt));
  const amountLabel = formatAmountPaise(r.amount, r.currency);
  const isFailed = String(payment.status || "").toLowerCase() === "failed";

  return (
    <motion.article
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="border-b border-sand-100 px-4 py-4 last:border-b-0"
    >
      <div className="flex items-center gap-3">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
            isFailed ? "bg-rose-50 text-rose-500" : "bg-sand-50 text-brand-600"
          }`}
        >
          {isFailed ? <XCircle size={19} /> : <Crown size={19} />}
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold text-ink-800">
            {r.planName || "Karanki Premium"}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-ink-500">
            <span className="inline-flex items-center gap-1">
              <Calendar size={11} /> {paidLabel}
            </span>
            {r.couponCode && (
              <span className="inline-flex items-center gap-1 font-medium text-emerald-700">
                <Tag size={10} /> {r.couponCode}
              </span>
            )}
          </div>
        </div>

        <div className="shrink-0 text-right">
          <p className={`text-[14px] font-semibold ${isFailed ? "text-rose-600" : "text-ink-800"}`}>
            {amountLabel}
          </p>
          <span
            className={`mt-1 inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider ${
              isFailed
                ? "text-rose-600"
                : "text-emerald-700"
            }`}
          >
            {isFailed ? (
              <><XCircle size={9} /> Failed</>
            ) : (
              <><CheckCircle2 size={9} /> Paid</>
            )}
          </span>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 pl-[52px]">
        {isFailed ? (
          <Link
            to="/settings/membership"
            className="inline-flex min-h-8 items-center gap-1.5 rounded-lg bg-brand-50 px-3 text-[11px] font-semibold text-brand-700 transition hover:bg-brand-100"
          >
            <Crown size={12} /> Try again
          </Link>
        ) : (
          <>
            <button
              type="button"
              onClick={onView}
              className="inline-flex min-h-8 items-center gap-1.5 rounded-lg px-3 text-[11px] font-semibold text-ink-600 transition hover:bg-sand-50 hover:text-brand-700"
            >
              <FileText size={12} /> Details
            </button>
            <button
              type="button"
              onClick={onDownload}
              className="inline-flex min-h-8 items-center gap-1.5 rounded-lg px-3 text-[11px] font-semibold text-brand-700 transition hover:bg-brand-50"
            >
              <Download size={12} /> Download PDF
            </button>
          </>
        )}
      </div>
    </motion.article>
  );
}

/* =====================================================================
   Payment Detail Bottom Sheet
===================================================================== */
function PaymentDetailSheet({ payment, onClose, onDownload }) {
  const r = paymentRecordToReceipt(payment);
  const paidAt = parseMembershipDate(r.paidAt);
  const validFrom = parseMembershipDate(r.validFrom);
  const validUntil = parseMembershipDate(r.validUntil);
  const amountLabel = formatAmountPaise(r.amount, r.currency);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[80] flex items-end justify-center bg-ink-900/50 backdrop-blur-sm sm:items-center sm:px-4"
    >
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", stiffness: 300, damping: 32 }}
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[86vh] w-full max-w-md flex-col overflow-hidden rounded-t-3xl bg-white shadow-elevated sm:max-h-[82vh] sm:rounded-3xl"
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-sand-100 px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-sand-50 text-brand-600">
              <Receipt size={17} />
            </div>
            <div className="min-w-0">
              <h3 className="text-[15px] font-semibold text-ink-800">Payment details</h3>
              <p className="truncate text-[11.5px] text-ink-500">{r.planName || "Karanki Premium"}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close payment details" className="btn-icon h-9 w-9">
            <X size={16} />
          </button>
        </div>

        <div className="flex shrink-0 items-center justify-between px-5 py-4">
          <div>
            <p className="text-[11px] font-medium text-ink-500">Amount paid</p>
            <p className="mt-0.5 font-display text-2xl font-bold leading-7 text-ink-800">{amountLabel}</p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
            <CheckCircle2 size={12} /> Paid
          </span>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-4">
          <div className="divide-y divide-sand-100 border-y border-sand-100">
            <DetailRow label="Plan" value={r.planName || "Premium"} />
            <DetailRow label="Duration" value={r.durationLabel || PREMIUM_DURATION_LABEL} />
            <DetailRow label="Valid until" value={formatMembershipDate(validUntil)} highlight />
          </div>

          <details className="group mt-3 rounded-2xl bg-sand-50/70 px-4">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-3 text-[12px] font-semibold text-ink-700 marker:content-none">
              <span>More payment information</span>
              <Plus size={15} className="shrink-0 text-ink-400 transition group-open:rotate-45" />
            </summary>
            <div className="space-y-3 border-t border-sand-200/70 py-3">
              <DetailRow label="Member" value={r.memberName || "-"} />
              <DetailRow label="Valid from" value={formatMembershipDate(validFrom)} />
              {r.couponCode && (
                <DetailRow
                  label="Coupon"
                  value={`${r.couponCode} (−${formatAmountPaise(r.discountAmount, r.currency)})`}
                  highlight
                />
              )}
              {r.paymentMethod && (
                <DetailRow
                  label="Paid via"
                  value={r.paymentMethodDetail ? `${r.paymentMethod} · ${r.paymentMethodDetail}` : r.paymentMethod}
                />
              )}
              <SmallRow label="Payment ID" value={r.receiptId || "-"} mono />
              <SmallRow label="Order ID" value={r.orderId || "-"} mono />
              <SmallRow label="Paid on" value={formatMembershipDate(paidAt)} />
            </div>
          </details>

          <p className="mt-3 text-center text-[10.5px] text-ink-400">
            System-generated receipt · No signature required
          </p>
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-sand-100 bg-white px-5 py-3.5">
          <button
            type="button"
            onClick={onDownload}
            className="btn-primary w-full py-3 text-[13px]"
          >
            <Download size={15} /> Download receipt
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function DetailRow({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between gap-3 py-3">
        <span className="shrink-0 text-[12px] text-ink-500">{label}</span>
        <span
          className={`min-w-0 text-right text-[12px] font-semibold ${
            highlight ? "text-brand-600" : "text-ink-800"
          }`}
        >
          {value}
        </span>
    </div>
  );
}

function SmallRow({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between gap-3 text-[12px]">
      <span className="shrink-0 text-ink-400">{label}</span>
      <span
        className={`text-right font-semibold text-ink-600 ${
          mono ? "font-mono text-[10.5px] break-all" : ""
        }`}
      >
        {value}
      </span>
    </div>
  );
}
